/**
 * Shape Manipulation Utilities
 * Handles polygon/tube shape creation, transformation, interaction, and history management
 */

/**
 * Creates pentagon points around a center with given radius
 */
export const createPentagonPoints = (center, radius) => {
  const lat = center.lat;
  const lng = center.lng;
  const points = [];
  
  for (let i = 0; i < 5; i++) {
    const angle = (i * 72 - 90) * (Math.PI / 180);
    const pointLat = lat + radius * Math.cos(angle);
    const pointLng = lng + radius * Math.sin(angle) / Math.cos(lat * Math.PI / 180);
    points.push({ lat: pointLat, lng: pointLng });
  }
  
  return points;
};

/**
 * Updates shape size by recalculating points
 */
export const updateShapeSize = (currentShape, searchMode, newSize, tubeThickness, setCurrentShape) => {
  if (!currentShape || !currentShape.center) return;
  
  const center = currentShape.center;
  const outerPoints = createPentagonPoints(center, newSize);
  
  let updatedShape;
  
  if (searchMode === 'polygon') {
    updatedShape = {
      ...currentShape,
      outerPoints,
      outerRadius: newSize
    };
  } else if (searchMode === 'tube') {
    const innerRadius = Math.max(0.002, newSize - tubeThickness);
    const innerPoints = createPentagonPoints(center, innerRadius);
    updatedShape = {
      ...currentShape,
      outerPoints,
      innerPoints,
      outerRadius: newSize,
      innerRadius
    };
  }
  
  setCurrentShape(updatedShape);
};

/**
 * Updates tube thickness
 */
export const updateTubeThickness = (currentShape, searchMode, newThickness, setCurrentShape) => {
  if (!currentShape || searchMode !== 'tube' || !currentShape.center) return;
  
  const center = currentShape.center;
  const outerRadius = currentShape.outerRadius;
  const innerRadius = Math.max(0.002, outerRadius - newThickness);
  const innerPoints = createPentagonPoints(center, innerRadius);
  
  const updatedShape = {
    ...currentShape,
    innerPoints,
    thickness: newThickness,
    innerRadius
  };
  
  setCurrentShape(updatedShape);
};

/**
 * Save shape state to history for undo/redo
 */
export const saveShapeToHistory = (shape, actionType, shapeHistory, historyIndex, setShapeHistory, setHistoryIndex) => {
  if (!shape) return;
  
  const newHistory = shapeHistory.slice(0, historyIndex + 1);
  newHistory.push({
    shape: JSON.parse(JSON.stringify(shape)),
    actionType,
    timestamp: Date.now()
  });
  
  // Limit history to 50 items
  if (newHistory.length > 50) {
    newHistory.shift();
  } else {
    setHistoryIndex(prev => prev + 1);
  }
  
  setShapeHistory(newHistory);
};

/**
 * Undo shape changes
 */
export const undoShapeChange = (historyIndex, shapeHistory, setHistoryIndex, setCurrentShape, toast) => {
  if (historyIndex > 0) {
    setHistoryIndex(prev => prev - 1);
    const previousState = shapeHistory[historyIndex - 1];
    setCurrentShape(JSON.parse(JSON.stringify(previousState.shape)));
    toast.success('Undone');
  } else {
    toast('No more undo steps available', {
      icon: 'ℹ️',
      duration: 2000
    });
  }
};

/**
 * Handle shape dragging start
 */
export const handleShapeDragStart = (event, currentShape, shapeInteractionMode, setIsShapeDragging, setShapeDragStart) => {
  if (!currentShape || shapeInteractionMode !== 'move') return;
  
  setIsShapeDragging(true);
  setShapeDragStart({
    lat: event.latLng.lat(),
    lng: event.latLng.lng(),
    shapeCenter: currentShape.center
  });
};

/**
 * Handle shape dragging
 */
export const handleShapeDrag = (event, isShapeDragging, shapeDragStart, currentShape, shapeInteractionMode, searchMode, shapeSize, tubeThickness, setCurrentShape) => {
  if (!isShapeDragging || !shapeDragStart || !currentShape || shapeInteractionMode !== 'move') return;
  
  const currentLat = event.latLng.lat();
  const currentLng = event.latLng.lng();
  
  const latOffset = currentLat - shapeDragStart.lat;
  const lngOffset = currentLng - shapeDragStart.lng;
  
  const newCenter = {
    lat: shapeDragStart.shapeCenter.lat + latOffset,
    lng: shapeDragStart.shapeCenter.lng + lngOffset
  };
  
  const outerPoints = createPentagonPoints(newCenter, currentShape.outerRadius || shapeSize);
  
  let updatedShape = {
    ...currentShape,
    center: newCenter,
    outerPoints
  };
  
  if (searchMode === 'tube' && currentShape.innerPoints) {
    const innerRadius = currentShape.innerRadius || (currentShape.outerRadius - tubeThickness);
    const innerPoints = createPentagonPoints(newCenter, innerRadius);
    updatedShape.innerPoints = innerPoints;
  }
  
  setCurrentShape(updatedShape);
};

/**
 * Handle shape drag end and save to history
 */
export const handleShapeDragEnd = (isShapeDragging, currentShape, shapeHistory, historyIndex, setIsShapeDragging, setShapeDragStart, setShapeHistory, setHistoryIndex) => {
  if (isShapeDragging && currentShape) {
    saveShapeToHistory(currentShape, 'move', shapeHistory, historyIndex, setShapeHistory, setHistoryIndex);
  }
  setIsShapeDragging(false);
  setShapeDragStart(null);
};

/**
 * Handle corner dragging for reshaping
 */
export const handleCornerDrag = (event, cornerIndex, isInner, currentShape, shapeInteractionMode, searchMode, setCurrentShape) => {
  if (!currentShape || shapeInteractionMode !== 'reshape') return;
  
  const newLat = event.latLng.lat();
  const newLng = event.latLng.lng();
  
  let updatedShape = { ...currentShape };
  
  if (isInner && searchMode === 'tube') {
    const newInnerPoints = [...currentShape.innerPoints];
    newInnerPoints[cornerIndex] = { lat: newLat, lng: newLng };
    updatedShape.innerPoints = newInnerPoints;
  } else {
    const newOuterPoints = [...currentShape.outerPoints];
    newOuterPoints[cornerIndex] = { lat: newLat, lng: newLng };
    updatedShape.outerPoints = newOuterPoints;
  }
  
  setCurrentShape(updatedShape);
};

/**
 * Handle corner drag end and save to history
 */
export const handleCornerDragEnd = (currentShape, shapeHistory, historyIndex, setIsDragging, setShapeHistory, setHistoryIndex) => {
  if (currentShape) {
    saveShapeToHistory(currentShape, 'corner_drag', shapeHistory, historyIndex, setShapeHistory, setHistoryIndex);
  }
  setIsDragging(false);
};

/**
 * Setup keyboard event listeners for undo and mode toggle
 */
export const setupKeyboardHandlers = (shapeInteractionMode, historyIndex, shapeHistory, setIsCtrlPressed, setShapeInteractionMode, setHistoryIndex, setCurrentShape, toast) => {
  const handleKeyDown = (event) => {
    if (event.ctrlKey) {
      setIsCtrlPressed(true);
      if (event.key === 'z' || event.key === 'Z') {
        event.preventDefault();
        undoShapeChange(historyIndex, shapeHistory, setHistoryIndex, setCurrentShape, toast);
      }
    }
    
    if (event.key === 'm' || event.key === 'M') {
      setShapeInteractionMode(prev => prev === 'move' ? 'reshape' : 'move');
      toast(`Shape interaction mode: ${shapeInteractionMode === 'move' ? 'Reshape' : 'Move'}`, {
        icon: 'ℹ️',
        duration: 2000
      });
    }
  };

  const handleKeyUp = (event) => {
    if (!event.ctrlKey) {
      setIsCtrlPressed(false);
    }
  };

  window.addEventListener('keydown', handleKeyDown);
  window.addEventListener('keyup', handleKeyUp);

  return () => {
    window.removeEventListener('keydown', handleKeyDown);
    window.removeEventListener('keyup', handleKeyUp);
  };
};

/**
 * Delete shape and reset related state
 */
export const handleShapeDelete = (setCurrentShape, setGeneratedCircles, setSelectedPolygonCircles, setShapeHistory, setHistoryIndex) => {
  setCurrentShape(null);
  setGeneratedCircles([]);
  setSelectedPolygonCircles(new Set());
  setShapeHistory([]);
  setHistoryIndex(-1);
};

/**
 * Initialize shape history when shape is first created
 */
export const initializeShapeHistory = (currentShape, shapeHistory, setShapeHistory, setHistoryIndex) => {
  if (currentShape && shapeHistory.length === 0) {
    setShapeHistory([{
      shape: JSON.parse(JSON.stringify(currentShape)),
      actionType: 'create',
      timestamp: Date.now()
    }]);
    setHistoryIndex(0);
  }
};